import type { ActionKind, Problem } from "@pagerless/schema";

/** "3:09 am" */
export function clock(ms: number): string {
  const d = new Date(ms);
  const h = d.getHours();
  const m = d.getMinutes().toString().padStart(2, "0");
  return `${h % 12 === 0 ? 12 : h % 12}:${m} ${h < 12 ? "am" : "pm"}`;
}

/** "3:09" (timeline column, no am/pm) */
export function shortClock(ms: number): string {
  const d = new Date(ms);
  const h = d.getHours();
  return `${h % 12 === 0 ? 12 : h % 12}:${d.getMinutes().toString().padStart(2, "0")}`;
}

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

/** "Wed 7:40 pm" */
export function dayClock(ms: number): string {
  return `${DAYS[new Date(ms).getDay()]} ${clock(ms)}`;
}

export function greeting(nowMs: number): string {
  const h = new Date(nowMs).getHours();
  if (h < 5) return "Hello";
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

/**
 * A duration for the LCD, the one formatter every screen uses (bug #15):
 * "45 SEC", "6 MIN 51 SEC", "2 HR 5 MIN". A zero second part is left off ("6 MIN", "2 HR").
 */
export function lcdDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  if (total < 60) return `${total} SEC`;
  const join = (big: string, small: number, unit: string) => (small > 0 ? `${big} ${small} ${unit}` : big);
  if (total < 3600) return join(`${Math.floor(total / 60)} MIN`, total % 60, "SEC");
  return join(`${Math.floor(total / 3600)} HR`, Math.floor((total % 3600) / 60), "MIN");
}

/** Text for the LCD: upper case, ASCII only, so VT323 renders every character. Apostrophes stay ("MAYA'S"). */
export function lcdText(s: string): string {
  return s
    .normalize("NFKD")
    .replace(/\p{M}/gu, "")
    .replace(/’/g, "'")
    .toUpperCase()
    .replace(/[^A-Z0-9 .,:>!?'\-_/[\]]/g, "");
}

/** "Maya's Shop" → "MAYA'S-SHOP" */
export function lcdSlug(s: string): string {
  return lcdText(s).trim().replace(/\s+/g, "-");
}

/** Plain words for each kind of fix, used in rules and prompts. */
export const KIND_LABEL: Record<ActionKind, string> = {
  restart: "Restart something that's stuck",
  add_capacity: "Add capacity when the app is busy",
  undo_update: "Undo an update",
  open_pr: "Write a proper fix for review",
  pause: "Pause the app",
  email_customers: "Email customers",
  data_change: "Change or delete saved data",
  custom: "Anything else",
};

/** "ask before undoing an update" */
export const KIND_VERB: Record<ActionKind, string> = {
  restart: "restarting something",
  add_capacity: "adding capacity",
  undo_update: "undoing an update",
  open_pr: "writing a fix",
  pause: "pausing the app",
  email_customers: "emailing customers",
  data_change: "changing saved data",
  custom: "doing anything else",
};

export type ProblemPhase = "needs_you" | "on_it" | "fixing" | "fixed" | "watching";

/** Which screen state a problem is in, from the person's point of view. */
export function problemPhase(
  problem: Problem,
  waitingForYes: boolean,
  /** A person was paged, or a fix failed: it needs someone even with no yes/no to give (bug 11). */
  needsPerson = false,
): ProblemPhase {
  if (problem.state === "resolved") return "fixed";
  if (problem.state === "fixing") return "fixing";
  if (problem.state === "acked") return "on_it";
  return waitingForYes || needsPerson ? "needs_you" : "watching";
}

/** "just now", "5 min ago", "2 hr ago", "3 days ago" */
export function ago(ms: number, nowMs: number): string {
  const s = Math.max(0, Math.round((nowMs - ms) / 1000));
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr ago`;
  const d = Math.floor(h / 24);
  return `${d} day${d === 1 ? "" : "s"} ago`;
}

/** Local time: "3:09 am" today, "Wed 7:40 pm" this week, else "12 Oct, 3:09 am". */
export function localTime(ms: number, nowMs: number): string {
  if (new Date(ms).toDateString() === new Date(nowMs).toDateString()) return clock(ms);
  if (Math.abs(nowMs - ms) < 6 * 86_400_000) return dayClock(ms);
  return `${new Date(ms).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}, ${clock(ms)}`;
}
